import { orange } from "@mui/material/colors";
import type { GridApiPremium } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import type { ThemeColorFn } from "@entrilia/shared/utilities/colorHelper";
import { makeModeAwareAccent } from "@entrilia/shared/utilities/colorHelper";
import { getVisibleRowIds } from "@entrilia/shared/utilities/dataGridApiHelper";
import type { WorkbookRun, WorkbookRunsProgress, WorkbookRunStatus } from "../../../../api/types/workbookRuns.types";

export const getFinishTime = (workbookRun: WorkbookRun) => {
  if (workbookRun.status === "Error") {
    return workbookRun.failedAt;
  }
  if (workbookRun.status === "Canceled") {
    return workbookRun.canceledAt;
  }
  if (workbookRun.status === "Completed") {
    return workbookRun.completedAt;
  }
  return null;
};

export const getStatusLabel = (status: WorkbookRunStatus): string => {
  switch (status) {
    case "InProgress":
      return "In Progress";
    case "Completed":
      return "Completed";
    case "Error":
      return "Error";
    case "Canceled":
      return "Canceled";
    case "Queued":
      return "Queued";
    default:
      return status;
  }
};

export const getStatusColor = (status: WorkbookRunStatus): ThemeColorFn => {
  switch (status) {
    case "InProgress":
      return (theme) => theme.palette.info.main;
    case "Completed":
      return (theme) => theme.palette.success.main;
    case "Error":
      return (theme) => theme.palette.error.main;
    case "Canceled":
      return (theme) => theme.palette.text.secondary;
    case "Queued":
      return (theme) => makeModeAwareAccent(theme, orange);
    default:
      return (theme) => theme.palette.text.secondary;
  }
};

const finishedStatuses = ["Completed", "Error", "Canceled", "Skipped"] as (WorkbookRunStatus | undefined)[];

export const isFinishedStatus = (status: WorkbookRunStatus | undefined): boolean => {
  return finishedStatuses.includes(status);
};

export const isDownloadableRun = (workbookRun: WorkbookRun | undefined): boolean =>
  Boolean(workbookRun?.catalogueId) && (workbookRun?.status === "Completed" || workbookRun?.status === "Error");

export const getProcessingRowIds = (apiRef: RefObject<GridApiPremium | null>): string[] => {
  if (!apiRef.current) {
    return [];
  }
  const visibleRowIds = getVisibleRowIds(apiRef);
  const visibleRows = visibleRowIds.map((id) => apiRef.current?.getRow<WorkbookRun>(id)).filter((r) => !!r);
  return visibleRows
    .filter((row) => !isFinishedStatus(row.status))
    .map((row) => row.operationId)
    .filter((id) => id !== undefined);
};

export const setRunsProgress = (currentRows: WorkbookRun[], data: WorkbookRunsProgress): WorkbookRun[] => {
  return currentRows.map((item) => {
    const progress = data?.result.find((r) => r.runId === item.id);
    if (!progress) {
      return item;
    }
    if (!isFinishedStatus(progress.currentStatus)) {
      return {
        ...item,
        status: progress.currentStatus,
        processedItems: progress.processedItems,
        startedAt: progress.startedAt,
      };
    }
    if (finishedStatuses.includes(progress.currentStatus)) {
      const newItem = {
        ...item,
        status: progress.currentStatus,
        processedItems: item.reportsSettings.length,
      };
      if (progress.currentStatus === "Completed") {
        newItem.completedAt = progress.finishedAt;
      }
      if (progress.currentStatus === "Error") {
        newItem.failedAt = progress.finishedAt;
      }
      if (progress.currentStatus === "Canceled") {
        newItem.canceledAt = progress.finishedAt;
      }
      return newItem;
    }

    return item;
  });
};

export const getJustFinishedRunIds = (rows: WorkbookRun[], data: WorkbookRunsProgress): string[] => {
  return data.result
    .filter(
      (progress) =>
        isFinishedStatus(progress.currentStatus) &&
        rows.some((row) => row.id === progress.runId && !isFinishedStatus(row.status))
    )
    .map((progress) => progress.runId);
};
